import React, { useState, useEffect } from 'react';
import AdminLayout from '../components/AdminLayout';
import EnhancedWordTable from '../components/EnhancedWordTable';
import { Button } from '@/components/ui/button';
import { getAllWordUsage } from '../lib/wordUsageApi';
import { cleanupNullReferences } from '../lib/admin-api';
import { Loader2, RefreshCw, Download, Trash2 } from 'lucide-react';
import { useToast } from '@/hooks/use-sonner-toast';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { WordUsageDocument } from '../types/wordUsage';

const WordAnalytics: React.FC = () => {
  const [wordUsage, setWordUsage] = useState<WordUsageDocument[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [cleaningUp, setCleaningUp] = useState<boolean>(false);
  const { toast } = useToast();

  useEffect(() => {
    fetchWordUsage();
  }, []);

  const fetchWordUsage = async () => {
    try {
      setLoading(true);

      // Get all words from the word_usage collection
      const words = await getAllWordUsage();
      setWordUsage(words);
    } catch (error) {
      console.error('Error fetching word usage:', error);
      toast({
        title: 'Error',
        description: 'Failed to load word usage data',
        variant: 'destructive',
      });
    } finally {
      setLoading(false);
    }
  };

  const handleRefresh = () => {
    fetchWordUsage();
  };

  const handleCleanupNullReferences = async () => {
    try {
      setCleaningUp(true);
      await cleanupNullReferences();

      toast({
        title: 'Success',
        description: 'Successfully cleaned up null references from database',
        variant: 'default',
      });

      // Refresh the data after cleanup
      await fetchWordUsage();
    } catch (error) {
      console.error('Error cleaning up null references:', error);
      toast({
        title: 'Error',
        description: 'Failed to cleanup null references',
        variant: 'destructive',
      });
    } finally {
      setCleaningUp(false);
    }
  };

  const handleExportCSV = () => {
    // Process the data for CSV export
    const processedData = wordUsage.map((word) => {
      // Get input and output counts
      const outputTotal = word.outputCount || 0;
      const inputTotal = word.inputCount || 0;

      // Get forge-specific counts
      const itemOutput = word.itemForgeCount || 0;
      const locationOutput = word.locationForgeCount || 0;
      const characterOutput = word.characterForgeCount || 0;
      const deconstructorOutput = word.deconstructorOutputCount || 0;

      // Calculate input counts for each forge type
      // This is an approximation since we don't have direct access to forge-specific input counts
      let itemInput = 0;
      let locationInput = 0;
      let characterInput = 0;
      let deconstructorInput = 0;

      // If we have inputToOutputs data, we can calculate forge-specific input counts
      if (word.inputToOutputs && typeof word.inputToOutputs === 'object') {
        Object.values(word.inputToOutputs).forEach((connection: any) => {
          if (connection && typeof connection === 'object' && connection.forgeTypes) {
            if (connection.forgeTypes.Item) itemInput += connection.forgeTypes.Item;
            if (connection.forgeTypes.Location) locationInput += connection.forgeTypes.Location;
            if (connection.forgeTypes.Character) characterInput += connection.forgeTypes.Character;
            if (connection.forgeTypes.Deconstructor)
              deconstructorInput += connection.forgeTypes.Deconstructor;
          }
        });
      }

      // Calculate I/O ratios
      const itemRatio =
        itemOutput > 0 && itemInput > 0 ? parseFloat((itemInput / itemOutput).toFixed(2)) : 0;

      const locationRatio =
        locationOutput > 0 && locationInput > 0
          ? parseFloat((locationInput / locationOutput).toFixed(2))
          : 0;

      const characterRatio =
        characterOutput > 0 && characterInput > 0
          ? parseFloat((characterInput / characterOutput).toFixed(2))
          : 0;

      const deconstructorRatio =
        deconstructorOutput > 0 && deconstructorInput > 0
          ? parseFloat((deconstructorInput / deconstructorOutput).toFixed(2))
          : 0;

      return {
        word: word.word,
        outputTotal,
        inputTotal,
        itemInput,
        itemOutput,
        locationInput,
        locationOutput,
        characterInput,
        characterOutput,
        deconstructorInput,
        deconstructorOutput,
        itemRatio,
        locationRatio,
        characterRatio,
        deconstructorRatio,
      };
    });

    // Generate CSV data with the new column structure
    const headers = [
      'Word',
      'Output Total',
      'Input Total',
      'Item I/O',
      'Location I/O',
      'Character I/O',
      'Deconstructor I/O',
    ];

    const csvData = [
      headers.join(','),
      ...processedData.map((item) =>
        [
          item.word,
          item.outputTotal,
          item.inputTotal,
          item.itemInput > 0 || item.itemOutput > 0 ? `${item.itemInput}/${item.itemOutput}` : '-',
          item.locationInput > 0 || item.locationOutput > 0
            ? `${item.locationInput}/${item.locationOutput}`
            : '-',
          item.characterInput > 0 || item.characterOutput > 0
            ? `${item.characterInput}/${item.characterOutput}`
            : '-',
          item.deconstructorInput > 0 || item.deconstructorOutput > 0
            ? `${item.deconstructorInput}/${item.deconstructorOutput}`
            : '-',
        ].join(',')
      ),
    ].join('\n');

    // Create a blob and download
    const blob = new Blob([csvData], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.setAttribute('href', url);
    link.setAttribute('download', `word-usage-${new Date().toISOString().split('T')[0]}.csv`);
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // Calculate statistics
  const totalInputCount = wordUsage.reduce((sum, word) => sum + (word.inputCount || 0), 0);
  const totalOutputCount = wordUsage.reduce((sum, word) => sum + (word.outputCount || 0), 0);
  const uniqueInputWords = wordUsage.filter((word) => (word.inputCount || 0) > 0).length;
  const uniqueOutputWords = wordUsage.filter((word) => (word.outputCount || 0) > 0).length;

  return (
    <AdminLayout title="Word Analytics">
      <div className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Total Words Used</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalInputCount + totalOutputCount}</div>
              <p className="text-xs text-muted-foreground mt-1">Across all forge types</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Unique Words</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{uniqueOutputWords}</div>
              <p className="text-xs text-muted-foreground mt-1">Unique words created</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Input Words</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalInputCount}</div>
              <p className="text-xs text-muted-foreground mt-1">Total input word usage</p>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-sm font-medium">Output Words</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="text-2xl font-bold">{totalOutputCount}</div>
              <p className="text-xs text-muted-foreground mt-1">Total output word usage</p>
            </CardContent>
          </Card>
        </div>

        <div className="flex justify-end mb-4">
          <div className="flex items-center gap-2">
            <Button variant="outline" size="sm" onClick={handleRefresh}>
              <RefreshCw className="h-4 w-4 mr-2" />
              Refresh
            </Button>
            <Button
              variant="outline"
              size="sm"
              onClick={handleCleanupNullReferences}
              disabled={cleaningUp}
              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50 dark:hover:bg-orange-950"
            >
              {cleaningUp ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
              ) : (
                <Trash2 className="h-4 w-4 mr-2" />
              )}
              {cleaningUp ? 'Cleaning...' : 'Cleanup Nulls'}
            </Button>
            <Button variant="outline" size="sm" onClick={handleExportCSV}>
              <Download className="h-4 w-4 mr-2" />
              Export CSV
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex items-center justify-center h-64">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : (
          <EnhancedWordTable
            words={wordUsage}
            title="Word Usage Analytics"
            onWordDeleted={fetchWordUsage}
          />
        )}
      </div>
    </AdminLayout>
  );
};

export default WordAnalytics;
